import type {
	TranscribeUrlInput,
	TranscribeUrlOutput,
} from "@maker/api-app/schemas/tool/transcriber/url";
import {
	type UseQueryOptions,
	useMutation,
	useQuery,
} from "@tanstack/react-query";
import { api } from "../services/api";
import { queryClient } from "../services/query-client";
import { unwrapApi, unwrapQuery } from "../utils/unwrap";

// import mock from "./mock.json";

type Transcription = TranscribeUrlOutput;

type CreateTranscriptionInput = TranscribeUrlInput;

const transcriptionKeys = (id: string) => ["transcription", id];

const useTranscriptionData = (id: string) => {
	const data = unwrapQuery<Transcription>(queryClient, transcriptionKeys(id));

	return data;
};

const useQueryTranscription = (
	id: string,
	options?: Omit<UseQueryOptions<Transcription>, "queryFn" | "queryKey">,
) => {
	return useQuery<Transcription>({
		queryKey: transcriptionKeys(id),
		staleTime: Infinity,
		gcTime: Infinity,
		...options,
	});
};

const useCreateTranscription = () => {
	return useMutation({
		mutationFn: async (input: CreateTranscriptionInput) => {
			const { data } = unwrapApi<Transcription>(
				await api.post({
					url: "/tool/transcriber",
					data: api.adapter.json(input),
				}),
			);

			// const { data } = mock;

			queryClient.setQueryData(transcriptionKeys(data.id), () => data);

			return data;
		},
	});
};

const useUpdateTranscription = () => {
	return useMutation({
		mutationFn: async (input: {
			id: string;
			speakers: Transcription["transcription"]["speakers"];
		}) => {
			queryClient.setQueryData<Transcription | undefined>(
				transcriptionKeys(input.id),
				(current) => {
					if (!current) {
						return current;
					}

					return {
						...current,
						transcription: {
							...current.transcription,
							speakers: input.speakers,
						},
					};
				},
			);
		},
	});
};

export {
	transcriptionKeys,
	useQueryTranscription,
	useTranscriptionData,
	useCreateTranscription,
	useUpdateTranscription,
	type CreateTranscriptionInput,
};
